import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {ReactElement, ReactNode} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {FolderSection} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LEGACY_FONT_PROXIMA_13, LEGACY_FONT_PROXIMA_17} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize} from 'util/NameUtil';
import {BasicEvent} from 'util/Types';

const {NOTEBOOK_SECTION, INVENTORY_SECTION} = FileBrowserConstants;

export function MoreDots({crumbs}: {crumbs: Array<CrumbLinkProps>}): JSX.Element {
  // Reverse order so deepest child is first
  const reversedCrumbs = crumbs.slice().reverse();
  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          {reversedCrumbs.map((crumb, i) => (
            <Option key={i} onSelect={crumb.onClick}>
              <span className="u-small-spacing-right">{crumb.icon}</span>
              {crumb.text}
            </Option>
          ))}
        </Menu>
      }
    >
      <BenchlingIcon icon="overflow" />
    </Dropdown>
  );
}

MoreDots.propTypes = {
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element,
      onClick: PropTypes.func,
      text: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
};

interface CrumbLinkProps {
  onClick?: ((e: BasicEvent) => void) | null;
  text: string;
  dataTestAttrs?: DataTestAttrs;
  icon?: ReactElement;
}

export function CrumbLink({
  className,
  dataTestAttrs,
  onClick,
  text,
  icon,
}: CrumbLinkProps & {className?: string}): JSX.Element {
  return (
    <span
      className={classNames('breadcrumbs-crumb', {'breadcrumbs-crumb--link': onClick != null}, className)}
      onClick={(e) => {
        if (onClick) {
          onClick(e);
          e.stopPropagation();
        }
      }}
      {...getTestProps(dataTestAttrs ?? {component: 'CrumbLink', element: 'root'})}
    >
      {icon}
      {icon ? ' ' : ''}
      {text}
    </span>
  );
}

CrumbLink.propTypes = {
  className: PropTypes.string,
  // Required as prop for MoreDots
  icon: PropTypes.element,
  onClick: PropTypes.func,
  text: PropTypes.string.isRequired,
};

interface SectionCrumbLinkProps extends CrumbLinkProps {
  onClick: (e: BasicEvent) => void;
  onChangeSection: (section: FolderSection) => void;
  section: FolderSection;
}

/**
 * Shows extra section info for currently listed item. Render Notebook/Inventory sectionSwitcher.
 */
export function SectionCrumbLink({
  text = '',
  icon = undefined,
  onClick,
  onChangeSection,
  section,
}: SectionCrumbLinkProps): JSX.Element {
  return (
    <span>
      <CrumbLink text={`${text}: `} icon={icon} onClick={onClick} />
      <span className="expandedBrowser-sectionSwitcher u-pointer">
        <Dropdown
          menu={
            <Menu value={section} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
              {[NOTEBOOK_SECTION, INVENTORY_SECTION].map((opt) => (
                <Option
                  key={opt}
                  value={opt}
                  // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                  onSelect={() => onChangeSection(opt)}
                  isSelected={section === opt}
                >
                  {capitalize(opt)}
                </Option>
              ))}
            </Menu>
          }
        >
          <span>
            <span className={css(styles.sectionCrumbText)}>{capitalize(section)}</span>{' '}
            <BenchlingIcon icon="chevron-down" />
          </span>
        </Dropdown>
      </span>
    </span>
  );
}

export function defaultRenderIcon(result?: unknown): JSX.Element {
  return <BenchlingIcon icon={result ? ModelTypeConstants.FOLDER : ModelTypeConstants.SOURCE} />;
}

interface BreadcrumbsProps {
  className?: string;
  crumbs: Array<ReactElement<CrumbLinkProps>>;
  settingsIcon?: ReactNode;
  showTrailingDivider?: boolean;
  divider?: ReactNode;
  trailingCrumbIcon?: ReactNode;
}

function BreadcrumbDivider({divider = '/'}: {divider?: ReactNode}): JSX.Element {
  return <span className="breadcrumbs-divider">{divider}</span>;
}

const dataTestAttrs = {component: 'Breadcrumbs', element: 'root'};

function Breadcrumbs(props: BreadcrumbsProps): JSX.Element {
  const {showTrailingDivider = false, divider} = props;
  const crumbs = props.crumbs;
  if (crumbs.length <= 3) {
    return (
      <div className={classNames('breadcrumbs', props.className)} {...getTestProps(dataTestAttrs)}>
        {/* eslint-disable-next-line no-restricted-syntax */}
        {_.flatten(
          crumbs.map((crumb, i) => {
            if (i === 0) {
              return crumb;
            }
            return [<BreadcrumbDivider key={`divider-${i}`} divider={divider} />, crumb];
          })
        )}
        {showTrailingDivider && <BreadcrumbDivider divider={divider} />}
        {props.trailingCrumbIcon}
        {props.settingsIcon}
      </div>
    );
  }
  // More than 3 crumbs, show MoreDots and last two crumbs.
  return (
    <div className={classNames('breadcrumbs', props.className)} {...getTestProps(dataTestAttrs)}>
      <MoreDots crumbs={crumbs.slice(0, -2).map((crumb) => crumb.props)} />
      <BreadcrumbDivider divider={divider} />
      {crumbs[crumbs.length - 2]}
      <BreadcrumbDivider divider={divider} />
      {crumbs[crumbs.length - 1]}
      {showTrailingDivider && <BreadcrumbDivider divider={divider} />}
      {props.trailingCrumbIcon}
      {props.settingsIcon}
    </div>
  );
}

export interface ListedCrumbItem {
  id?: string;
  api_identifier?: string;
  parent?: ListedCrumbItem | null;
  name: string;
}

/**
 * Returns a list of crumbs for a listed.
 *
 * listed:              object with {parent: item} recursively until the root; each item has {id, name}
 * openResult:          onClick function for listed crumb
 * renderListedCrumb:   optional function to render a listed item; defaults to rendering CrumbLink
 * onGoToRoot:          onClick function for root; must be provided if rootText is provided
 * rootText:            root text; renders a crumb for the root if provided
 * showSectionDropdown: boolean to indicate whether to render SectionCrumbLink
 * section:             section value (Notebook, Inventory)
 * shouldLinkLastCrumb: boolean to indicate whether to render the last item as clickable
 */
export function getCrumbsFromListed({
  listed,
  openResult,
  renderListedCrumb,
  onGoToRoot,
  rootText,
  renderIcon,
  showSectionDropdown,
  section,
  onChangeSection,
  shouldLinkLastCrumb,
}: {
  listed?: ListedCrumbItem | null;
  openResult: (item: unknown) => void;
  renderListedCrumb?: (item: ListedCrumbItem) => ReactElement;
  onGoToRoot?: () => void;
  rootText?: string;
  renderIcon?: (item?: ListedCrumbItem) => ReactElement | undefined;
  showSectionDropdown?: boolean;
  section?: FolderSection;
  onChangeSection?: (section: FolderSection) => void;
  shouldLinkLastCrumb?: boolean;
}): Array<ReactElement> {
  const crumbs = [];
  let cur: ListedCrumbItem | undefined | null = listed;
  while (cur) {
    // TODO(T2222): change folder id in API response to api_identifier
    if (cur.api_identifier) {
      cur = {...cur, id: cur.api_identifier};
    }
    const onClick = openResult.bind(null, cur);
    const icon = renderIcon ? renderIcon(cur) : defaultRenderIcon(cur);
    // cur.name is empty when loading
    const crumbText = cur.name || '';
    // Allow renderListedCrumb function to render crumb. If not specified, render
    // CrumbLink or SectionCrumbLink (for section dropdowns).
    let crumb = renderListedCrumb ? renderListedCrumb(cur) : null;
    if (!crumb) {
      if (!cur.parent && showSectionDropdown) {
        assert(Boolean(onChangeSection && section), 'onChangeSection and section must be defined');
        crumb = (
          <SectionCrumbLink
            key={`crumb-${cur.id}`}
            text={crumbText}
            icon={icon}
            onClick={onClick}
            section={section!}
            onChangeSection={onChangeSection!}
          />
        );
      } else {
        crumb = (
          <CrumbLink
            key={`crumb-${cur.id}`}
            text={crumbText}
            icon={icon}
            onClick={cur.id !== listed?.id || shouldLinkLastCrumb ? onClick : null}
          />
        );
      }
    }
    crumbs.push(crumb);
    cur = cur.parent;
  }
  if (rootText) {
    crumbs.push(
      <CrumbLink
        key="crumb-source"
        text={rootText}
        icon={renderIcon ? renderIcon() : defaultRenderIcon()}
        onClick={listed ? onGoToRoot : null}
      />
    );
  }
  crumbs.reverse();
  return crumbs;
}

/**
 * Renders crumbs to emphasize the last crumb and de-emphasize the rest (shown as a path above).
 */
export function SplitBreadcrumbs(
  props: Pick<BreadcrumbsProps, 'crumbs' | 'settingsIcon' | 'trailingCrumbIcon'>
): JSX.Element {
  const pathCrumbs = props.crumbs.slice(0, -1);
  const currentCrumbs = props.crumbs.slice(-1);
  return (
    <div className={css(styles.splitCrumbs)}>
      {pathCrumbs.length > 0 && (
        <Breadcrumbs className={css(styles.pathCrumbs)} crumbs={pathCrumbs} showTrailingDivider={true} />
      )}
      <div className={css(styles.currentCrumbsContainer)}>
        <Breadcrumbs
          className={css(styles.currentCrumbs)}
          crumbs={currentCrumbs}
          settingsIcon={<span className={css(styles.splitCrumbsIcons)}>{props.settingsIcon}</span>}
          trailingCrumbIcon={<span className={css(styles.splitCrumbsIcons)}>{props.trailingCrumbIcon}</span>}
        />
      </div>
    </div>
  );
}

SplitBreadcrumbs.propTypes = {
  crumbs: PropTypes.array.isRequired,
  settingsIcon: PropTypes.node,
};

/**
 * Renders a root crumb (e.g. for a section)
 */
export function RootCrumb(props: {text: string}): JSX.Element {
  return <SplitBreadcrumbs crumbs={[<CrumbLink key="root" text={props.text} />]} />;
}

RootCrumb.propTypes = {
  text: PropTypes.string.isRequired,
};

const styles = StyleSheet.create({
  pathCrumbs: {
    color: Colors.GRAY_DARKER,
    ...LEGACY_FONT_PROXIMA_13,
    marginBottom: '3px',
  },
  currentCrumbs: {
    ...LEGACY_FONT_PROXIMA_17,
    lineHeight: '28px',
    overflowWrap: 'anywhere',
    // splitCrumbs is a flex container, so set minWidth to avoid overflow for long crumbs.
    minWidth: 0,
  },
  sectionCrumbText: {
    borderBottom: `1px dashed ${Colors.BLUE_LIGHT}`,
    ':hover': {
      color: Colors.BLUE,
      borderBottom: `1px dashed ${Colors.BLUE}`,
    },
  },
  splitCrumbs: {
    display: 'flex',
    flexDirection: 'column',
  },
  currentCrumbsContainer: {
    display: 'flex',
    alignItems: 'baseline',
  },
  splitCrumbsIcons: {
    whiteSpace: 'nowrap',
  },
});

export default Breadcrumbs;
